import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { persist, createJSONStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { mergeDeepLeft } from "ramda";
import devDebug from "../Helpers/debug";
import i18n from "i18next";
import all_cpv_trees from "../Assets/CPVs/all_cpv_trees.json";
import { useTabStore } from "./tabStore";
import { useMapsStore } from "./mapsStore";
import { useCPVStore } from "./cpvStore";
import cloneDeep from "lodash/cloneDeep";
import idbKeyvalAdapter from "./1_utility";
// import { current } from "immer";


const starterStrategy = {
  name: i18n.t("Strategy"),
  email: "",
  nuts: [],
  cpvs: [],
  mepa_only: false,
  keywords: "",
  strategy_search_choice: "CPV_OR_KEYWORD",
  cpv_count: [],
};

// define the initial state
const initialState = {
  user: {
    usercolumnview: {
      platform_id: true,
      title: true,
      estimated_total_value: true,
      closing_date: true,
      cpv: true,
      tender_national_id: false,
      publication_date: false,
      tender_url: false,
      tendering_authority: false,
      currency: false,
      procedure_title: false,
      procedure_type: false,
      short_description: false,
      award_criteria: false,
      soa_categories: false,
      nut: true,
    },
  },
  userLoaded: false,
  reloadCPVs: false,
  editableStrategies: { tender_strategy: {}, edit_user: {} },
  success: false,
  metric: "total_price",
  getError: false,
  submitErrors: { all: "" },
  errType: { all: "" },
  tutorialEnabled: true,
  cpvTrees: { all: all_cpv_trees[i18n.language.slice(0, 2)] },
  CPVStage: "loading",
  strategiesStatus: "loading",
  unsavedWarning: 0,
  addStrategyOnLoad: false,
  checkboxCPVs: {},
  normalCPVs: {},
  instance: null,
  enablePDF: false,
  updateInstance: () => { },
  existingStrategies: null,
  triggerStrategyReload: Math.random()
};
const currentAppVersionFromEnv = Number(import.meta.env.VITE_VERSION);

export const useTenderStrategyStore = createWithEqualityFn(
  persist(
    immer((set, get) => ({
      ...initialState,
      // ------------------- USER --------------
      setUser: (payload) =>
        set((state) => {
          state.user = payload;
        }),
      // ------------------- PDF --------------
      setInstanceAndUpdateInstance: (instance, updateInstance) =>
        set((state) => {
          state.instance = instance;
          state.updateInstance = updateInstance;
        }),
      updatedColumn: (column, checked) =>
        set((state) => {
          state.user.usercolumnview[column] = checked;
        }),
      setUserLoaded: (payload) =>
        set((state) => {
          state.userLoaded = payload;
        }),
      setEnablePDF: (enablePDF) =>
        set((state) => {
          state.enablePDF = enablePDF;
        }),
      // ------------------- EXISTING TENDER STRATEGIES -----------------------
      setExistingStrategies: (existingStrategies) =>
        set((state) => {
          state.existingStrategies = cloneDeep(existingStrategies); //cloning here as using resp from API would make object read-only
        }),
      setStrategiesStatus: (payload) =>
        set((state) => {
          devDebug(`setStrategiesStatus -> payload: ${payload}`);
          state.strategiesStatus = payload;
        }),
      setAddStrategyOnLoad: (addStrategyOnLoad) =>
        set((state) => {
          state.addStrategyOnLoad = addStrategyOnLoad;
        }),
      setEditableStrategiesWithExistingStrategies: (section) =>
        set((state) => {
          const strategiesToKeep = {};
          for (let key of Object.keys(state.editableStrategies[section])) {
            if (key.includes("_new")) {
              strategiesToKeep[state.editableStrategies[section][key].id] = state.editableStrategies[section][key];
            }
          }
          state.editableStrategies[section] = {
            ...state.existingStrategies,
            ...strategiesToKeep,
          };
          if (Object.keys(state.editableStrategies[section]).length === 0) {
            devDebug(`setEditableStrategies -> all strategies is empty`);
            const id = Math.random() + "_new";
            state.editableStrategies[section][id] = {
              ...starterStrategy,
              id: id,
              name: i18n.t("New Strategy"),
              addedAsFiller: false,
            };
            useMapsStore.getState().addDefaultMapData(section, id);
            useMapsStore.getState().addDefaultMapState(section, id);
            useCPVStore.getState().addDefaultCPVData(section, id);
            useTabStore.getState().setTabID(section, id);
            devDebug(`setEditableStrategies -> all strategies is: `, cloneDeep(state.editableStrategies));
            state.strategiesStatus = "loaded";
          } else {
            for (let id of Object.keys(state.editableStrategies[section])) {
              let payload = cloneDeep(state.editableStrategies[section][id].cpvs)
              let convertedPayload = {};
              if (Array.isArray(payload)) {
                for (let cpv of payload) {
                  convertedPayload[cpv] = true;
                }
                payload = convertedPayload;
              }
              state.editableStrategies[section][id].cpvs = Object.keys(payload).filter(
                (key) => !payload?.[key]?.partialChecked
              );
              let withPartialCheckedObj = {};
              let withoutPartialCheckedObj = {};
              let hasPartialAttribute = false;
              if (Object.keys(payload).length > 0 && payload[Object.keys(payload)[0]].hasOwnProperty("partialChecked")) {
                hasPartialAttribute = true;
              }
              if (hasPartialAttribute) {
                state.editableStrategies[section][id].checkboxCPVs = payload;
                for (let cpv of Object.keys(payload)) {
                  if (!payload[cpv].partialChecked) {
                    withoutPartialCheckedObj[cpv] = true;
                  }
                }
                state.editableStrategies[section][id].normalCPVs = withoutPartialCheckedObj;
              } else {
                for (let cpv of Object.keys(payload)) {
                  withPartialCheckedObj[cpv] = { checked: true, partialChecked: false };
                }
                state.editableStrategies[section][id].checkboxCPVs = withPartialCheckedObj;
                state.editableStrategies[section][id].normalCPVs = payload;
              }
            }
            state.strategiesStatus = "loaded";
          }
        }),
      setEditableStrategies: (newStrategies, section) =>
        set((state) => {
          devDebug(`tenderStrategyStore -> setEditableStrategies`);
          const strategiesToKeep = {};
          for (let key of Object.keys(state.editableStrategies[section])) {
            if (key.includes("_new")) {
              strategiesToKeep[state.editableStrategies[section][key].id] = state.editableStrategies[section][key];
            }
          }
          state.editableStrategies[section] = {
            ...newStrategies,
            ...strategiesToKeep,
          };
          if (Object.keys(state.editableStrategies[section]).length === 0) {
            devDebug(`setEditableStrategies -> all strategies is empty`);
            const id = Math.random() + "_new";
            state.editableStrategies[section][id] = {
              ...starterStrategy,
              id: id,
              name: i18n.t("New Strategy"),
              addedAsFiller: false,
            };
            useMapsStore.getState().addDefaultMapData(section, id);
            useMapsStore.getState().addDefaultMapState(section, id);
            useCPVStore.getState().addDefaultCPVData(section, id);
            useTabStore.getState().setTabID(section, id);
            devDebug(`setEditableStrategies -> all strategies is: `, cloneDeep(state.editableStrategies));
            state.strategiesStatus = "loaded";
          } else {
            for (let id of Object.keys(state.editableStrategies[section])) {
              let payload = cloneDeep(state.editableStrategies[section][id].cpvs)
              let convertedPayload = {};
              if (Array.isArray(payload)) {
                for (let cpv of payload) {
                  convertedPayload[cpv] = true;
                }
                payload = convertedPayload;
              }
              state.editableStrategies[section][id].cpvs = Object.keys(payload).filter(
                (key) => !payload?.[key]?.partialChecked
              );
              let withPartialCheckedObj = {};
              let withoutPartialCheckedObj = {};
              let hasPartialAttribute = false;
              if (Object.keys(payload).length > 0 && payload[Object.keys(payload)[0]].hasOwnProperty("partialChecked")) {
                hasPartialAttribute = true;
              }
              if (hasPartialAttribute) {
                state.editableStrategies[section][id].checkboxCPVs = payload;
                for (let cpv of Object.keys(payload)) {
                  if (!payload[cpv].partialChecked) {
                    withoutPartialCheckedObj[cpv] = true;
                  }
                }
                state.editableStrategies[section][id].normalCPVs = withoutPartialCheckedObj;
              } else {
                for (let cpv of Object.keys(payload)) {
                  withPartialCheckedObj[cpv] = { checked: true, partialChecked: false };
                }
                state.editableStrategies[section][id].checkboxCPVs = withPartialCheckedObj;
                state.editableStrategies[section][id].normalCPVs = payload;
              }
            }
            state.strategiesStatus = "loaded";
          }
        }),
      addStrategy: (section, addedAsFiller) =>
        set((state) => {
          devDebug(`addStrategy -> section: ${section}, addedAsFiller: ${addedAsFiller}`);
          const id = Math.random() + "_new";
          state.editableStrategies[section][id] = {
            ...starterStrategy,
            id: id,
            name: i18n.t("New Strategy"),
            addedAsFiller: addedAsFiller,
          };
          useMapsStore.getState().addDefaultMapData(section, id);
          useMapsStore.getState().addDefaultMapState(section, id);
          useCPVStore.getState().addDefaultCPVData(section, id);
          useTabStore.getState().setTabID(section, id);
          if (section === "tender_strategy") {
            state.unsavedWarning = 1;
          }
          devDebug(`addStrategy -> all strategies is: `, cloneDeep(state.editableStrategies));
        }),
      deleteUnsavedStrategy: (section, id) =>
        set((state) => {
          delete state.editableStrategies[section][id];
          let unsaved = false;
          if (section === "tender_strategy") {
            for (let strat of Object.keys(state.editableStrategies[section])) {
              devDebug(`This is a key: ${strat}`);
              if (strat.includes("_new")) {
                unsaved = true;
              }
            }
            if (unsaved) {
              state.unsavedWarning = 1;
            } else {
              state.unsavedWarning = 0;
            }
          }
          devDebug(`were there unsaved strategies? ${unsaved}`);
        }),
      cleanUpFillerStrategies: () =>
        set((state) => {
          devDebug(`tenderStrategyStore -> cleaning up filler strategies`);
          const currentTab = useTabStore.getState().currentTab["edit_user"];
          for (let key of Object.keys(state.editableStrategies["edit_user"])) {
            devDebug(`this is a key: ${key} and currentTab: ${currentTab}`);
            if (state.editableStrategies["edit_user"][key].addedAsFiller) {
              devDebug(`this is a filler strategy`);
              if (key !== currentTab) {
                devDebug(`key did not equal the current tab, deleting ${key}`);
                delete state.editableStrategies["edit_user"][key];
              }
            }
          }
        }),

      setName: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].name = payload;
        }),
      setEmail: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].email = payload;
        }),
      setMepaOption: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].mepa_only = payload;
        }),
      setSearchChoice: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].strategy_search_choice = payload;
        }),
      setKeywords: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].keywords = payload;
        }),
      addStrategyFromServer: (payload, section) =>
        set((state) => {
          state.editableStrategies[section][payload.id] = payload;
        }),
      setSubmitError: (err, section, id) =>
        set((state) => {
          state.submitErrors[id] = err;
        }),
      setErrType: (err, section, id) =>
        set((state) => {
          state.errType[id] = err;
        }),

      // ------------------- UPDATE NUTS AND CPV DATA -----------------------
      setCPVs: (payload, section, id) =>
        set((state) => {
          let convertedPayload = {};
          if (Array.isArray(payload)) {
            for (let cpv of payload) {
              convertedPayload[cpv] = true;
            }
            payload = convertedPayload;
          }

          state.editableStrategies[section][id].cpvs = Object.keys(payload).filter(
            (key) => !payload?.[key]?.partialChecked
          );
          let withPartialCheckedObj = {};
          let withoutPartialCheckedObj = {};
          let hasPartialAttribute = false;
          if (Object.keys(payload).length > 0 && payload[Object.keys(payload)[0]].hasOwnProperty("partialChecked")) {
            hasPartialAttribute = true;
          }
          if (hasPartialAttribute) {
            state.editableStrategies[section][id].checkboxCPVs = payload;
            for (let cpv of Object.keys(payload)) {
              if (!payload[cpv].partialChecked) {
                withoutPartialCheckedObj[cpv] = true;
              }
            }
            state.editableStrategies[section][id].normalCPVs = withoutPartialCheckedObj;
          } else {
            for (let cpv of Object.keys(payload)) {
              withPartialCheckedObj[cpv] = { checked: true, partialChecked: false };
            }
            state.editableStrategies[section][id].checkboxCPVs = withPartialCheckedObj;
            state.editableStrategies[section][id].normalCPVs = payload;
          }
        }),

      deleteCPV: (cpv, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].cpvs = state.editableStrategies[section][id].cpvs.filter(
            (cpvID) => cpvID !== cpv
          );
          delete state.editableStrategies[section][id].checkboxCPVs[cpv];
          delete state.editableStrategies[section][id].normalCPVs[cpv];
        }),
      setNUTS: (payload, section, id) =>
        set((state) => {
          state.editableStrategies[section][id].nuts = payload;
        }),

      // ------------------- OTHER -----------------------
      setTriggerStrategyReload: () =>
        set((state) => {
          state.triggerStrategyReload = Math.random();
        }),
      setTutorialEnabled: (payload) => set(() => ({ tutorialEnabled: payload })),
      resetState: () => {
        useTenderStrategyStore.persist.clearStorage();
        set(initialState);
      },
    })),
    {
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              ![
                "enablePDF",
                "strategiesStatus",
                "user",
                "userLoaded",
                "animatedOnce",
                "dashboardDataLoading",
              ].includes(key)
          )
        ),
      name: "tenderStrategy",
      storage: createJSONStorage(() => idbKeyvalAdapter),
      version: currentAppVersionFromEnv,
      migrate: (persistedState, storedVersion) => {
        devDebug(
          `[tenderStrategyStore Migrate] Called! Stored: ${storedVersion} (type: ${typeof storedVersion}), App: ${currentAppVersionFromEnv} (type: ${typeof currentAppVersionFromEnv})`
        );
        if (storedVersion !== currentAppVersionFromEnv) {
          devDebug('[tenderStrategyStore Migrate] Versions differ. Resetting to initialState.');
          return initialState;
        }
        if (persistedState === null || persistedState === undefined) {
          devDebug('[tenderStrategyStore Migrate] Persisted state is null/undefined despite version match. Resetting.');
          return initialState;
        }
        devDebug('[tenderStrategyStore Migrate] Versions same. Using persisted state.');
        return persistedState;
      },
      // merge: (persistedState, currentState) => {
      //   devDebug('[tenderStrategyStore Merge] Merging state...');
      //   if (typeof persistedState !== 'object' || persistedState === null) {
      //     devDebug('[tenderStrategyStore Merge] Persisted state is not an object, returning current state or initialState.');
      //     return initialState;
      //   }
      //   return mergeDeepLeft(persistedState, currentState);
      // },

      onRehydrateStorage: (state) => {
        devDebug(`[tenderStrategyStore onRehydrateStorage] Attempting rehydration...`);
        return (hydratedState, error) => {
          if (error) {
            console.error(
              `[tenderStrategyStore] Failed to rehydrate store!`,
              error
            );
            devDebug(
              `[tenderStrategyStore] Error during rehydration. Store name: "tenderStrategyStore".`,
              "Error object:", error,
              "State passed to onRehydrateStorage:", state, // This is the raw value from storage before parsing/migration
              "Hydrated state (if available):", hydratedState // This is the state *after* potential migration/parsing, if it got that far
            );
          } else {
            // devDebug(
            //   `[tenderStrategyStore] Successfully rehydrated. Hydrated state:`,
            //   hydratedState
            // );
          }
        };
      },
    }
  ),
  shallow
);
